import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getJobCloseAtDialogOpen } from 'lib/selectors/getJobCloseAtDialog';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import { TickerContext } from 'components/TickerProvider';
import _get from 'lodash/get';
import {
  toISO,
  safeIsBefore,
  safeConvertToISO,
  isValidTimestamp,
} from 'lib/utils/date';
import { getCompany } from 'lib/selectors/getCompany';
import { getLatestNonCurrentIdleInterval } from 'lib/selectors/getLatestNonIdleInterval';
import { getLastPartTimestamp } from 'lib/selectors/getLastPartTimestamp';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { actionCloseJobCloseAtDialog } from 'lib/actions';
import { getLatestOperatorRun } from 'lib/selectors/getLatestOperatorRun';
import JobCloseAtDialogEventEmitter from './emitter';

/**
 * JobCloseAtDialogBase Component
 *
 * A modal dialog that allows users to select when a job should end.
 * Provides three options: "Now", "Last active", and "Last part produced at".
 * Options are automatically disabled based on logical constraints to prevent
 * selecting invalid end times.
 *
 * @component
 * @returns {JSX.Element|null} The dialog component or null if not open
 */
const JobCloseAtDialogBase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nowMs = useContext(TickerContext);

  const company = useSelector(getCompany);
  const latestNonCurrentIdleInterval = useSelector(
    getLatestNonCurrentIdleInterval
  );
  const latestOperatorRun = useSelector(getLatestOperatorRun);
  const lastPartTimestamp = useSelector(getLastPartTimestamp);
  const latestActivity = useSelector(getLatestActivity);

  // Get timezone from company
  const tzid = _get(company, 'timezone');

  // Extract raw values to satisfy exhaustive-deps rule
  const latestNonIdleIntervalEnd = latestNonCurrentIdleInterval?.end;
  const latestActivityStart = latestActivity?.start;
  const latestOperatorRunStartAt = latestOperatorRun?.startAt;

  /**
   * Memoized converted time values to avoid redundant timezone conversions
   * Only calculate when dialog is open for performance
   */
  const convertedTimes = useMemo(() => {
    return {
      lastActiveAt: safeConvertToISO(latestNonIdleIntervalEnd, tzid),
      lastActivityAt: safeConvertToISO(latestActivityStart, tzid),
      lastPartProducedAt: safeConvertToISO(lastPartTimestamp, tzid),
      operatorRunStartAt: safeConvertToISO(latestOperatorRunStartAt, tzid),
    };
  }, [
    latestNonIdleIntervalEnd,
    latestActivityStart,
    lastPartTimestamp,
    latestOperatorRunStartAt,
    tzid,
  ]);

  /**
   * Generates the list of available end time options
   *
   * Each option represents a different point in time when the job could end:
   * - "Now": Current time
   * - "Last part produced at": When the last part was produced (or "Unavailable" if no data)
   * - "Last active": When the user was last active (or "Unavailable" if no data)
   *
   * Options are disabled when data is unavailable or when timing logic
   * would create invalid job end scenarios.
   *
   * @returns {Array<Object>} Array of option objects with id, label, value, and disabled status
   */
  const optionsList = useMemo(() => {
    const {
      lastActiveAt,
      lastActivityAt,
      lastPartProducedAt,
      operatorRunStartAt,
    } = convertedTimes;

    const options = [
      {
        id: 'now',
        label: t('Now'),
        value: toISO(nowMs),
        disabled: false,
      },
    ];

    // Always show "Last part produced at" option
    if (isValidTimestamp(lastPartProducedAt)) {
      const isLastPartProducedDisabled =
        safeIsBefore(lastPartProducedAt, lastActivityAt) ||
        safeIsBefore(lastPartProducedAt, operatorRunStartAt);

      options.push({
        id: 'last-part-produced',
        label: t('Last part produced at'),
        value: lastPartProducedAt,
        disabled: isLastPartProducedDisabled,
      });
    } else {
      options.push({
        id: 'last-part-produced',
        label: t('Last part produced at'),
        value: t('Unavailable'),
        disabled: true,
      });
    }

    // Always show "Last active" option
    if (isValidTimestamp(lastActiveAt)) {
      const isLastActiveDisabled =
        safeIsBefore(lastActiveAt, lastActivityAt) ||
        safeIsBefore(lastActiveAt, operatorRunStartAt);

      options.push({
        id: 'last-active',
        label: t('Last active'),
        value: lastActiveAt,
        disabled: isLastActiveDisabled,
      });
    } else {
      options.push({
        id: 'last-active',
        label: t('Last active'),
        value: t('Unavailable'),
        disabled: true,
      });
    }

    return options;
  }, [convertedTimes, t, nowMs]);

  /**
   * Closes the dialog and resets the event emitter
   */
  const closeJobCloseAtDialog = useCallback(() => {
    dispatch(actionCloseJobCloseAtDialog());
  }, [dispatch]);

  /**
   * Handles dialog close action
   * Emits cancel event and closes the dialog
   */
  const handleClose = useCallback(() => {
    JobCloseAtDialogEventEmitter.fireAndForget('cancel');
    closeJobCloseAtDialog();
  }, [closeJobCloseAtDialog]);

  /**
   * Handles option selection
   * Emits success event with selected value and closes the dialog
   *
   * @param {string} selectedValue - The selected time value
   */
  const handleSelect = useCallback(
    (selectedValue) => {
      JobCloseAtDialogEventEmitter.fireAndForget('success', selectedValue);
      closeJobCloseAtDialog();
    },
    [closeJobCloseAtDialog]
  );

  return (
    <BaseJobTimeDialog
      headerText={t('Select job end time')}
      optionsList={optionsList}
      tzid={tzid}
      onClose={handleClose}
      onSelect={handleSelect}
    />
  );
};

const JobCloseAtDialog = () => {
  // Redux selectors for component state
  const isOpen = useSelector(getJobCloseAtDialogOpen);

  if (!isOpen) return null;

  return <JobCloseAtDialogBase />;
};

export default JobCloseAtDialog;
